// components/CtaButton.tsx
import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { CircularProgress } from "@mui/material";

const baseSx = {
    textTransform: "none",
    borderRadius: "9999px",
    pl: 2,
    pr: 1,
    py: 1.1,
    fontSize: 16,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    boxShadow: "none",
};

const baseSxStart = {
    textTransform: "none",
    borderRadius: "9999px",
    pl: 1,
    pr: 2,
    py: 1.1,
    fontSize: 16,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    boxShadow: "none",
};

export default function CtaButton({
    cta,
    label: labelProp,
    href: hrefProp,
    endIconNode,
    endIconSrc = "/img/header/Vector.svg",
    sx,
    onClick,
    iconPosition = "end",
    disableRipple = true,
    arrowWidth = 15,
    size,
    loader,
    variant = "primary", // "primary" = teal, "secondary" = gray
    ...rest
}) {
    const label = labelProp ?? cta?.label ?? "Submit";
    const href = hrefProp ?? cta?.href;
    const clickHandler = onClick ?? cta?.onClick;

    // style presets
    const variantStyles =
        variant === "primary"
            ? {
                background: "linear-gradient(180deg, #7fd0cb 0%, #0f7f8a 100%)",
                color: "#fff",
                "&:hover": {
                    background: "linear-gradient(180deg, #74c4bf 0%, #0e747f 100%)",
                },
            }
            : {
                background: "#d1d5db",
                color: "#fff",
                "&:hover": { background: "#c4c4c4" },
            };

    const roundIconSx = {
        ml: 0.5,
        width: size === "small" ? 24 : 30,
        height: size === "small" ? 24 : 30,
        borderRadius: "50%",
        bgcolor: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    };

    const EndIcon =
        endIconNode ?? (
            <Box sx={roundIconSx}>
                {loader ? (
                    <CircularProgress
                        sx={{
                            width: "20px !important",
                            height: "20px !important",
                            color: variant === "primary" ? "#0f7f8a" : "#6b7280",
                        }}
                    />
                ) : (
                    <Image
                        src={endIconSrc}
                        alt=""
                        width={arrowWidth}
                        height={arrowWidth}
                        style={{
                            filter: variant === "secondary" ? "invert(0.4)" : "none",
                        }}
                    />
                )}
            </Box>
        );

    const commonProps = {
        disableRipple,
        sx: [iconPosition === "start" ? baseSxStart : baseSx, variantStyles, sx],
        onClick: clickHandler,
        ...rest,
    };

    if (href) {
        return iconPosition === "start" ? (
            <Button component={Link} href={href} {...commonProps}>
                {EndIcon}
                {label}
            </Button>
        ) : (
            <Button component={Link} href={href} {...commonProps}>
                {label}
                {EndIcon}
            </Button>
        );
    }

    return iconPosition === "start" ? (
        <Button {...commonProps}>
            {EndIcon}
            {label}
        </Button>
    ) : (
        <Button {...commonProps}>
            {label}
            {EndIcon}
        </Button>
    );
}
